import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LifecycleBandView } from "@/types/dashboard/genie-console";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface LayoutProps {
  bands: LifecycleBandView[];
}

/**
 * Direction B — "Stage".
 *
 * Editorial and full-bleed. Each stage is a deep brand wash with the lifecycle
 * word set very large as page imagery in its own right, the state line beside
 * it, and the live content floating on a white card that overlaps the wash.
 * Feels like a magazine spread rather than a list of cards.
 */
export const StageLayout = ({ bands }: LayoutProps) => (
  <div className="space-y-8">
    {bands.map((band, index) => (
      <section key={band.id} className="overflow-hidden rounded-[26px]">
        {/* The wash — brand gradient, band word as the imagery. */}
        <div
          className="relative px-6 pb-16 pt-7 md:px-10 md:pb-24 md:pt-10"
          style={{
            background: `linear-gradient(115deg, ${band.tint} 0%, ${band.tint}c4 55%, ${band.tint}8a 100%)`,
          }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-2 bottom-6 select-none text-[84px] font-extrabold leading-none tracking-[-4px] text-white/15 md:text-[150px] md:tracking-[-8px]"
          >
            {String(index + 1).padStart(2, "0")}
          </span>

          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/25">
                  <GenieIcon name={band.genieIcon} size={18} color="#FFFFFF" />
                </span>
                <span className="text-xs font-bold tracking-[3px] text-white/80">
                  STAGE {index + 1}
                </span>
              </div>
              <h2 className="text-4xl font-extrabold leading-none tracking-[-1.5px] text-white md:text-6xl">
                {band.label}
              </h2>
              <p className="mt-2 max-w-xl text-sm text-white/85 md:text-base">
                {band.tagline} — {band.stateLine}
              </p>
            </div>

            <Link
              to={band.linkTo}
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-white/95 px-5 py-2.5 text-sm font-semibold shadow-lg transition-all hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              style={{ color: band.tint }}
            >
              {band.linkLabel}
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Live content, overlapping the wash. */}
        <div className="-mt-10 px-4 md:-mt-16 md:px-10">
          <div className="rounded-[20px] bg-white p-4 shadow-xl md:p-6">{band.preview}</div>
        </div>

        <div className="h-4" />
      </section>
    ))}
  </div>
);

export default StageLayout;
